import React, { useState, useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { PaymentOrder } from '../../types';
import Button from '../Form/Button';
import Input from '../Form/Input';
import TextArea from '../Form/TextArea';
import { useProviders } from '../../hooks/useProviders';
import { useBanks } from '../../hooks/useBanks';
import { resolveCurrencyCode } from '../../utils/currencyUtils';
import { generateEnglishTemplate, generateArabicTemplate, getPrintHtml } from './PaymentOrderLetterTemplate';

interface PaymentOrderLetterGeneratorProps {
  paymentOrder: PaymentOrder;
  initialLanguage?: 'en' | 'ar';
  onClose: () => void;
  onGenerate: (data: PaymentOrderLetterData) => void;
}

interface PaymentOrderLetterData {
  paymentOrderId: string;
  language: 'en' | 'ar';
  customContent?: string;
  recipient?: string;
  notes?: string;
}

const PaymentOrderLetterGenerator: React.FC<PaymentOrderLetterGeneratorProps> = ({
  paymentOrder,
  initialLanguage = 'en',
  onClose,
  onGenerate
}) => {
  const [language, setLanguage] = useState<'en' | 'ar'>(initialLanguage);
  const [previewContent, setPreviewContent] = useState('');
  const { data: providersData } = useProviders();
  const { data: banksData } = useBanks();

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
    watch,
    setValue
  } = useForm<PaymentOrderLetterData>({
    defaultValues: {
      paymentOrderId: paymentOrder.id,
      language: initialLanguage,
      customContent: '',
      recipient: '',
      notes: ''
    }
  });

  const provider = useMemo(() => {
    if (!providersData || providersData.length === 0) return null;
    return providersData.find((p: any) => (p.documentId || p.id) === paymentOrder.providerId);
  }, [paymentOrder.providerId, providersData]);

  const bank = useMemo(() => {
    if (!paymentOrder.bankId || !banksData || banksData.length === 0) return null;
    return banksData.find((b: any) => (b.documentId || b.id) === paymentOrder.bankId);
  }, [paymentOrder.bankId, banksData]);

  useEffect(() => {
    const template = language === 'en' 
      ? generateEnglishTemplate(paymentOrder, provider || null, bank || null)
      : generateArabicTemplate(paymentOrder, provider || null, bank || null);
    setPreviewContent(template);
    setValue('customContent', template);
  }, [language, setValue, paymentOrder, provider, bank]);

  const handleLanguageChange = (newLanguage: 'en' | 'ar') => {
    setLanguage(newLanguage);
    setValue('language', newLanguage);
  };

  const onSubmit = (data: PaymentOrderLetterData) => {
    onGenerate({
      ...data,
      language
    });
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const content = watch('customContent') || previewContent;
      printWindow.document.write(getPrintHtml(content, paymentOrder.orderNumber, language));
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleSaveAsPDF = () => {
    alert('PDF generation feature would be implemented with a proper PDF library like jsPDF or Puppeteer');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl h-[92vh] overflow-hidden">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Generate Payment Order Document
              </h2>
              <p className="text-sm text-gray-600">
                Order #{paymentOrder.orderNumber} - {provider?.name} - {resolveCurrencyCode(paymentOrder.currency)} {paymentOrder.total.toLocaleString()}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex flex-1 min-h-0 overflow-hidden">
            {/* Left Panel - Configuration */}
            <div className="w-1/4 p-6 border-r border-gray-200 overflow-y-auto">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Language
                  </label>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => handleLanguageChange('en')}
                      className={`px-3 py-2 rounded-md text-sm font-medium ${
                        language === 'en'
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      English
                    </button>
                    <button
                      type="button"
                      onClick={() => handleLanguageChange('ar')}
                      className={`px-3 py-2 rounded-md text-sm font-medium ${
                        language === 'ar'
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      العربية
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-700">Order Information</h3>
                  <div className="text-xs text-gray-600 space-y-1">
                    <p><strong>Order #:</strong> {paymentOrder.orderNumber}</p>
                    <p><strong>Date:</strong> {new Date(paymentOrder.date).toLocaleDateString()}</p>
                    <p><strong>Status:</strong> {paymentOrder.status}</p>
                    <p><strong>Items:</strong> {paymentOrder.items.length}</p>
                  </div>
                </div>

                <div>
                  <Input
                    label="Additional Recipient"
                    {...register('recipient')}
                    placeholder="Optional recipient information"
                  />
                </div>

                <div>
                  <TextArea
                    label="Notes"
                    {...register('notes')}
                    placeholder="Additional notes"
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Button type="submit" variant="primary" disabled={isSubmitting} className="w-full">
                    Generate Document
                  </Button>
                  <Button type="button" variant="secondary" onClick={handlePrint} className="w-full">
                    Print
                  </Button>
                  <Button type="button" variant="secondary" onClick={handleSaveAsPDF} className="w-full">
                    Save as PDF
                  </Button>
                </div>
              </form>
            </div>

            {/* Right Panel - Preview */}
            <div className="flex-1 p-6 overflow-y-auto">
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Document Preview</h3>
                <p className="text-sm text-gray-600">
                  {language === 'en' ? 'English' : 'Arabic'} payment order document
                </p>
              </div>
              
              <div className="border rounded-lg p-6 bg-gray-50">
                <div className="bg-white p-6 rounded shadow-sm">
                  <TextArea
                    {...register('customContent')}
                    value={watch('customContent') || previewContent}
                    rows={40}
                    className="font-mono text-xs leading-tight"
                    style={{ 
                      direction: language === 'ar' ? 'rtl' : 'ltr',
                      fontFamily: '"Courier New", monospace'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentOrderLetterGenerator;
